import React from "react";
import CacheService from "../../../core/services/cache/cache_service";
import LocalStorageService from "../../../core/services/cache/local_storage_service";

export interface AuthenticationState {
    loggedIn: boolean,
    authenticationToken: string | null,
}


export const AuthenticationContext = React.createContext<AuthenticationState | null>(null);


export const AuthenticationProvider: React.FC<React.PropsWithChildren<any>> = ({children}) => {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [state, setState] = React.useState<AuthenticationState | null>(null);

    /**
     * Creates the authentication state and sets it to the context.
     */
    const constructAndSetAuthenticationState = React.useCallback(() => {
        const isLoggedIn = CacheService.isLoggedIn();
        const authToken = CacheService.retrieveTokenAndExpiration()?.token;
        setState({
            loggedIn: isLoggedIn,
            authenticationToken: authToken,
        })
    }, [])

    /**
     * Sets the initial state of the authentication context.
     */
    React.useEffect(() => {
        constructAndSetAuthenticationState();
        setLoading(false);
    }, [constructAndSetAuthenticationState])

    /**
     * Listens to changes in the local storage and updates the authentication state accordingly.
     */
    React.useEffect(() => {
        const storageListener = (event: StorageEvent) => {
            switch (event.key) {
                case LocalStorageService.keys.token:
                case LocalStorageService.keys.tokenExpiration:
                case LocalStorageService.eventKeys.logout:
                    constructAndSetAuthenticationState();
                    break;
                default:
                    if (event.key === null && event.storageArea && !event.storageArea.length && event.newValue === null && event.oldValue === null) {
                        // This is a clear event, we need to update the authentication state.
                        constructAndSetAuthenticationState();
                    }
                    break;
            }
        };
        window.addEventListener('storage', storageListener, {passive: true});
        return () => {
            window.removeEventListener('storage', storageListener);
        }
    }, [constructAndSetAuthenticationState])

    return (
        <>
            <AuthenticationContext.Provider value={state}>
                {
                    !loading &&
                    children
                }
            </AuthenticationContext.Provider>
        </>
    );
}

/**
 * A hook to provide access to the authentication state.
 */
export const useAuthenticationState = () => {
    const state = React.useContext(AuthenticationContext);
    if (!state) {
        throw new Error('useAuthentication must be used within a AuthenticationProvider');
    }
    return state;
}